import { ApexOptions } from 'apexcharts';
import { GRANULARITIES, ResultSet, TimeDimensionGranularity } from '@cubejs-client/core';
import moment from 'moment';
import { authorizationService } from '@/container';
import Vue from 'vue';
type ApexChartType = ApexChart['type'];
type AvailableChartTypes = Extract<ApexChartType, 'pie' | 'bar' | 'line' | 'radar' | 'area'> | 'table' ;

export function isApexChartType(chartType: AvailableChartTypes): chartType is Extract<ApexChartType, AvailableChartTypes> {
	return chartType !== 'table';
}

export function hasTimeDimensions(resultSet: ResultSet) {
	const query = resultSet.query();
	return query.timeDimensions && query.timeDimensions.length;
}

export function dateFormater(date: Date, granularity: TimeDimensionGranularity | undefined): string {
	const user = authorizationService.getCurrentUser();
	const languageCode = user.languageCode.toLowerCase();
	const m = moment(date).locale(languageCode);
	switch (granularity) {
		case 'second':
			return m.format('D MMM YYYY HH:mm:ss');
		case 'minute':
			return m.format('D MMM YYYY HH:mm');
		case 'hour':
			return m.format('D MMM YYYY HH');
		case 'day':
			return m.format('D MMM YYYY');
		case 'week':
			return m.format('Wo') + ' ' + m.format('MMM YY');
		case 'month':
			return m.format('MMM YYYY');
		case 'quarter':
			return m.format('Qo YYYY');
		case 'year':
			return m.format('YYYY');
		default:
			return m.format('D MMM YYYY HH:mm:ss');
	}
}

export function formatDate(date: Date, format: string) {
	const user = authorizationService.getCurrentUser();
	const languageCode = user.languageCode.toLowerCase();
	const m = moment(date).locale(languageCode);
	return m.format(format);
}

abstract class Transformer {
	abstract getChartData: (type: AvailableChartTypes, resultSet: ResultSet) => Record<string, unknown>;

	protected seriesValues(resultSet: ResultSet): unknown[] {
		return resultSet.series();
	}
}

class ChartHelper {
	private dataTransformer: Transformer;
	constructor(transformer: Transformer) {
		this.dataTransformer = transformer;
	}

	getChartData(type: AvailableChartTypes, resultSet: ResultSet): Record<string, unknown> {

		return this.dataTransformer.getChartData(type, resultSet);
	}
}
type ApexChartData = {
	series: unknown[],
	options: ApexOptions
}
interface IGranularity {
	name?: string;
	title: string;
}

class ApexChartTransformer extends Vue {

	granularities: IGranularity[] = GRANULARITIES;
	groupedByWeek = false;
	colors: string[] = [];
	getChartData = (type: AvailableChartTypes, resultSet: ResultSet, additionalConfig: any | null = null): ApexChartData => {
		// const query = resultSet.query();
		// const series = resultSet.series();
		// const seriesNames = resultSet.seriesNames();
		// const pivot = resultSet.pivot();
		// const chartPivot = resultSet.chartPivot();
		this.colors = [];
		switch (type) {
			case 'line':
			case 'area':
				return this.getLineAreaChartData(type, resultSet, additionalConfig);
			case 'pie':
				return this.getPieChartData(resultSet, additionalConfig);
			case 'bar':
				return this.getBarChartData(resultSet, additionalConfig);
			default:
				return {
					series: [],
					options: {}
				};
		}
	}
	public setGroupedByWeek(value) {
		this.groupedByWeek = value;
	}
	private getLineAreaChartData(type: Extract<ApexChartType, AvailableChartTypes>, resultSet: ResultSet, additionalConfig: any | null = null): ApexChartData {
		// const hasTimeDemensions = this.hasTimeDimensions(resultSet)
		const result = {
			series: this.series(resultSet, additionalConfig),
			options: {
				chart: {
					type: type
				}
			} as ApexOptions
		};
		if (this.areAdditionalColorDefined()) {
			result.options.colors = this.colors;
		}
		/* At the moment STEP requested to not support this through custom code, but if in the future requirements gonna change, this is what I was working on before being stopped
		if (!_.isNil(additionalConfig) && !_.isNil(additionalConfig.isPercentage) && additionalConfig.isPercentage) {
			(result.options as any).dataLabels = {
				formatter: (val, { seriesIndex, w}) => {
					const sum = w.globals.initialSeries[seriesIndex].data.map(val => +val.y).reduce((a, b) => a + b, 0);
					return `${((+val / sum) * 100).toFixed(1)}%`;
				}
			};
		}*/
		return result;
	}

	private loadAdditionalConfig(additionalConfig: any | null, valueToCheck: any) {
		if (!_.isNil(additionalConfig)) {
			// check if colors are present
			if (!_.isNil(additionalConfig.colors) && _.isArray(additionalConfig.colors)) {
				const colors = additionalConfig.colors.find(x => x.value === valueToCheck);
				if (!_.isNil(colors) && !_.isNil(colors.fillColor))
					this.colors.push(colors.fillColor);
			}
		}
	}

	private getPieChartData(resultSet: ResultSet, additionalConfig: any | null = null): ApexChartData {

		const series = resultSet.series();
		series[0].series.map(el => {
			const arr = el.x.split(',');
			for (let i = 0; i < arr.length; i++) {
				this.loadAdditionalConfig(additionalConfig, arr[i]);
				arr[i] = this.$t(arr[i]);
			}
			el.x = arr.join(',');
		});
		const result = {
			options: {
				chart: {
					type: additionalConfig.isDonut ? 'donut' : 'pie'
				},
				legend: {
					offsetY: 20
				},
				dataLabels: {
					formatter: function (val, opts) {
						let isPercentage = false;
						if (!_.isNil(additionalConfig) && !_.isNil(additionalConfig.isPercentage))
							isPercentage = additionalConfig.isPercentage;

						return isPercentage ? `${(val as any).toFixed(1)}%` : opts.w.config.series[opts.seriesIndex];
					},
				},
				labels: series[0].series.map(x => this.$t(x.x)),
				...(this.areAdditionalColorDefined() && { colors: this.colors })

			},
			series: series[0].series.map(x => x.value)
		} as ApexChartData;
		return result;
	}

	private getBarChartData(resultSet: ResultSet, additionalConfig: any | null = null): ApexChartData {

		return {
			series: this.series(resultSet, additionalConfig),// [{ data: [['true', 23652], ['false', 9246]] }]
			options: {
				chart: {
					type: 'bar'
				},
				...(this.areAdditionalColorDefined() && { colors: this.colors })
				// xaxis: hasTimeDemensions ? { type: 'datetime' } : undefined
			},

		};
	}

	series(resultSet: ResultSet, additionalConfig: any | null = null): unknown[] {
		const seriesNames = resultSet.seriesNames();
		const pivot = resultSet.chartPivot();
		pivot.map(el => {
			const arr = el.x.split(',');
			for (let i = 0; i < arr.length; i++) {
				arr[i] = this.$t(arr[i]);
			}
			el.x = arr.join(',');
		});

		const series: unknown[] = [];
		seriesNames.forEach(e => {
			const data = pivot.map((p) => { return { x: this.$t(p.x), y: p[e.key] }; });
			let title = e.title;

			if (title.includes(','))
				title = title.split(',')[0];
			this.loadAdditionalConfig(additionalConfig, title);
			series.push({ name: this.$t(title), data });
		});
		const hasTimeDemensions = hasTimeDimensions(resultSet);
		if (hasTimeDemensions) {

			if (this.groupedByWeek) {
				series.forEach((s: any) => {
					s.data = this.getWeekResult(s.data);
				});
			}
			else {
				const query = resultSet.query();

				const timeDimension = query.timeDimensions![0];
				const granularity = timeDimension.granularity;
				series.forEach((s: any) => {
					s.data.forEach(d => {
						d.x = dateFormater(new Date(d.x), granularity);
					});
				});
			}
		}
		return series;
	}

	areAdditionalColorDefined() {
		return !_.isNil(this.colors) && _.isArray(this.colors) && this.colors.length > 0;
	}

	getWeekResult(data: any) {
		data.forEach(d => {
			d.x = dateFormater(new Date(d.x), 'week');
		});
		return this.groupByWeek(data);
	}

	//grouping data with 'day' granularity by week
	groupByWeek(data: any): any {
		const result = Array.from(data.reduce(
			(m, { x, y }) => m.set(x, (m.get(x) || 0) + y), new Map
		), ([x, y]) => ({ x, y }));
		return result;
	}

	protected pairs(resultSet: ResultSet): unknown[] {
		return resultSet.series()[0].series.map((item) => [item.x, item.value]);
	}

}

export { ChartHelper, ApexChartTransformer, AvailableChartTypes, ApexChartData };

